import React from "react";
import { Link } from "react-router-dom";
import { useCompare, COMPARE_MAX } from "../contexts/CompareContext";

/**
 * CompareBar — floating action strip at the bottom-center of the
 * viewport whenever the buyer has at least ONE item in their compare
 * list. Design goal: an ambient "you've stashed X pieces to weigh
 * against each other — here's the button to open the side-by-side"
 * that nudges the buyer to commit without feeling pushy.
 *
 * Hidden entirely on the /compare page itself so the CTA doesn't
 * cover the recommendation panel it's already displaying.
 */
export default function CompareBar() {
  const { ids, count, clear } = useCompare();
  if (count === 0) return null;
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/compare")) return null;

  return (
    <div
      className="fixed bottom-6 inset-x-0 z-[70] flex justify-center pointer-events-none"
      data-testid="compare-bar"
    >
      <div className="pointer-events-auto flex items-center gap-3 bg-midnight-deep/95 backdrop-blur border border-gold/40 rounded-full pl-4 pr-2 py-2 shadow-[0_20px_40px_-16px_rgba(0,0,0,0.6)]">
        <span className="text-[11px] uppercase tracking-widest text-white/70">
          Compare · {count}/{COMPARE_MAX}
        </span>
        <div className="flex items-center gap-1">
          {ids.map((_, i) => (
            <span
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-gold"
              aria-hidden
            />
          ))}
          {Array.from({ length: COMPARE_MAX - count }).map((_, i) => (
            <span key={`e${i}`} className="w-1.5 h-1.5 rounded-full bg-white/20" aria-hidden />
          ))}
        </div>
        <button
          type="button"
          onClick={clear}
          className="text-[10px] uppercase tracking-widest text-white/50 hover:text-white/80 px-2"
          data-testid="compare-bar-clear"
        >
          Clear
        </button>
        <Link
          to="/compare"
          data-testid="compare-bar-open"
          className={`bg-gold text-black text-[11px] uppercase tracking-widest rounded-full px-4 py-1.5 hover:bg-gold-hover ${count < 2 ? "opacity-60 pointer-events-none" : ""}`}
        >
          {count < 2 ? "Add one more →" : `Compare ${count} →`}
        </Link>
      </div>
    </div>
  );
}
